import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { connect } from "react-redux";

import Media, { ScreenWidth } from "css/Media";

import WP from "WP";
import api from "api";
import { setCurrentView, setImageGallery } from "store";
import modules from "modules";

import Hero from "./PageHero";
import Container from "partials/Container";
import Image from "partials/Image";
import Html from "partials/Html";
import IconSelector from "partials/IconSelector";
import HotelCard from "partials/HotelCard/HotelCard";
import RoomTypeCard from "partials/RoomTypeCard/RoomTypeCard";
import RoomTypeCarousel from "./RoomTypeCarousel";
import ImageOverlay from "partials/ImageOverlay";
import Overlay from "partials/Overlay";
import ErrorBoundary from "partials/ErrorBoundary";
import Footer from "partials/Footer";
import GridElement from "partials/GridElement";
import Backdrop from "partials/Backdrop";
import CookieNotice from "partials/CookieNotice";

const HotelPage = (props) => {
  const [hotel, setHotel] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [fixedSearch, setFixedSearch] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);

  const elHeightRef = useRef(0);
  const _setElHeight = (data) => {
    elHeightRef.current = data;
  };

  const { accommodation, availability } = props;
  //console.log("[DEBUG] HotelPageprops", { accommodation, availability });

  useEffect(() => {
    checkScreenSize();
    preparePage();
    window.addEventListener("resize", checkScreenSize);
    window.addEventListener("scroll", setFixedPosition);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
      window.removeEventListener("scroll", setFixedPosition);
    };
  }, []);

  useEffect(() => {
    preparePage();
  }, [props.match.params.slug]);

  //Set the initial height of the availability search when a hotel exists (because that means the markup also does)
  useEffect(() => {
    if (hotel) {
      let el = isMobile
        ? document.getElementById("mobile-availability-checker")
        : document.getElementById("availability-checker");
      if (el) {
        let elDistanceToTop = Math.floor(el.getBoundingClientRect().top);
        _setElHeight(elDistanceToTop);
      } else {
        //console.log("Target element was not found: ", el);
      }
      getRoomTypes();
    }
  }, [hotel]);

  const getRoomTypes = () => {
    let addAvailability = hotel.acf.room_types.map((rt) => {
      rt.availability = availability.hotels.find(
        (h) => h.id == rt.visbook_room_id
      );
      return rt;
    });
    let availableRoomTypes = addAvailability.sort((a, b) => {
      if (a.availability && b.availability) {
        return a.availability.available === b.availability.available
          ? 0
          : a.availability.available
          ? -1
          : 1;
      } else return null;
    });
    setRoomTypes(availableRoomTypes);
  };

  const setFixedPosition = () => {
    let height = elHeightRef.current;
    if (height) {
      let fixedSearch = false;
      let scrollHeight = window.scrollY;

      if (scrollHeight >= height) {
        fixedSearch = true;
      }

      setFixedSearch(fixedSearch);
    }
  };

  const preparePage = () => {
    window.scrollTo(0, 0);
    api.accommodationFetcher.getAll().then(() => {
      api.accommodationFetcher
        .getBySlug({ slug: props.match.params.slug })
        .then((r) => {
          let page = r.data[0];
          props.setCurrentView(page);
          setHotel({ ...page });
          //console.log("[DEBUG] page", page);
          scrapeForImages(page);
        });
    });
  };

  const scrapeForImages = (hotel) => {
    let arr = [];

    //console.log("[DEBUG] hotel", hotel);

    //Images always included on hotel page
    arr.push({ image: hotel.acf.featured_image });
    hotel.acf.images.map((image) => arr.push({ image: image.image }));

    //Images added through modules
    if (hotel.acf.modules) {
      let twoImagesArr = hotel.acf.modules.filter(
        (module) => module.acf_fc_layout === "two_images"
      );

      if (twoImagesArr.length) {
        twoImagesArr.map((module) => {
          arr.push({ image: module.image_one });
          arr.push({ image: module.image_two });
          return null;
        });
      }

      let imageModuleArr = hotel.acf.modules.filter(
        (module) => module.acf_fc_layout === "image"
      );

      if (imageModuleArr.length) {
        imageModuleArr.map((module) => {
          arr.push({ image: module.image });
          return null;
        });
      }
    }
    props.setImageGallery(arr);
  };

  const checkScreenSize = () => {
    let mobile = false;

    if (ScreenWidth.lessThan("s")) {
      mobile = true;
    }
    setIsMobile(mobile);
  };

  if (hotel) {
    return (
      <Wrapper>
        <ErrorBoundary for="Hotel hero">
          <Hero item={hotel} isMobile={isMobile} fixed={fixedSearch} />
        </ErrorBoundary>
        <ErrorBoundary for="Hotel description">
          <Container grid>
            <GridElement
              colStart={{ default: 1, s: 7 }}
              colEnd={{ default: 8, s: 18 }}
              rowStart={1}
            >
              <StyledP>{hotel.acf.description}</StyledP>
            </GridElement>
            <GridElement
              colStart={{ default: 1, s: 7 }}
              colEnd={{ default: 8, s: 18 }}
              rowStart={2}
            >
              <BulletPoints>
                {hotel.acf.key_features.map((feature, idx) => {
                  return (
                    <li key={idx}>
                      <IconSelector icon={feature.icon} />
                      <span>{feature.feature_name}</span>
                    </li>
                  );
                })}
              </BulletPoints>
            </GridElement>
            {hotel.acf.html_module ? (
              <GridElement
                colStart={{ default: 1, s: 7 }}
                colEnd={{ default: 8, s: 13 }}
                rowStart={3}
              >
                <TripAdvisorScript content>
                  {hotel.acf.html_module}
                </TripAdvisorScript>
              </GridElement>
            ) : null}
            <GridElement
              colStart={{ default: 1, s: 7 }}
              colEnd={{ default: 8, s: 18 }}
              rowStart={hotel.acf.html_module ? 4 : 3}
            >
              <Html content>{hotel.acf.rich_text}</Html>
            </GridElement>
          </Container>
        </ErrorBoundary>
        <ErrorBoundary for="Hotel images">
          <Images>
            <Container grid>
              {hotel.acf.images.map((image, idx) => (
                <GridElement
                  colStart={{ default: 1, s: idx % 2 == 0 ? 13 : 1 }}
                  colEnd={{ default: 8, s: idx % 2 == 0 ? 22 : 10 }}
                  rowStart={{ default: "auto", s: idx % 2 == 0 ? 1 : 3 }}
                  rowEnd={{ default: "auto", s: idx % 2 == 0 ? 4 : 6 }}
                  rowSpan
                  key={image.image.id}
                >
                  <ImageOverlay image={image.image}>
                    <StyledImage image={image.image} />
                  </ImageOverlay>
                </GridElement>
              ))}
            </Container>
          </Images>
        </ErrorBoundary>
        <ErrorBoundary for="Room types">
          <RoomTypeWrapper id="room-types">
            {availability.searched ? (
              roomTypes.map((roomType, idx) => {
                return (
                  <RoomTypeCard
                    room={roomType}
                    key={idx}
                    hotelName={hotel.acf.item_name}
                  />
                );
              })
            ) : (
              <RoomTypeCarousel
                hotel={hotel}
                isMobile={isMobile}
                roomTypes={roomTypes}
              />
            )}
          </RoomTypeWrapper>
        </ErrorBoundary>
        {hotel.acf.modules && (
          <ErrorBoundary for="Activity Builder">
            {hotel.acf.modules.map((module, i) => {
              return (
                <div key={i} className={"module-" + module.acf_fc_layout}>
                  {React.createElement(modules[module.acf_fc_layout], {
                    data: module,
                  })}
                </div>
              );
            })}
          </ErrorBoundary>
        )}
        <MoreHotels>
          <Container grid>
            <GridElement
              colStart={{ default: 1, s: 3 }}
              colEnd={{ default: 8, s: 22 }}
            >
              <h3>Flere hoteller</h3>
            </GridElement>
            {accommodation
              .filter((filterHotel) => filterHotel.id !== hotel.id)
              .map((filterHotel, idx) => (
                <CardGridElement
                  colStart={{ default: 1, s: idx % 2 == 0 ? 3 : 13 }}
                  colEnd={{ default: 8, s: idx % 2 == 0 ? 12 : 22 }}
                  rowStart={{ default: idx + 2, s: 2 }}
                  key={idx}
                >
                  <HotelCard hotel={filterHotel} />
                </CardGridElement>
              ))}
          </Container>
        </MoreHotels>
        <Overlay />
        <Backdrop />
        <ErrorBoundary for="Footer">
          <Footer />
        </ErrorBoundary>
        <CookieNotice />
      </Wrapper>
    );
  } else return null;
};

const mapStateToProps = (state) => {
  return {
    page: state.currentView,
    accommodation: state.accommodation.posts[WP.lang]
      ? state.accommodation.posts[WP.lang]
      : [],
    availability: state.availability,
  };
};

const actions = {
  setCurrentView,
  setImageGallery,
};

export default connect(mapStateToProps, actions)(HotelPage);

const Wrapper = styled.section`
  padding-top: 134px;

  ${Media.greaterThan("m")`
    padding-top: 132px;
  `}

  ${Media.greaterThan("l")`
    padding-top: 149.5px;
  `}
`;

const StyledP = styled.p`
  ${(props) => props.theme.fonts.h3}
  padding-top: ${(props) => props.theme.spacing.elementL};
`;

const BulletPoints = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: ${(props) => props.theme.spacing.elementM} 0;

  li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 50%;

    ${Media.greaterThan("m")`
      width: 33%;
    `}

    span {
      ${(props) => props.theme.fonts.p}
    }
  }
`;

const Images = styled.div`
  width: 100%;
  padding: ${(props) => props.theme.spacing.blockM} 0;

  .container {
    ${Media.greaterThan("s")`
      grid-template-rows: repeat(5, 1fr);
    `}
  }
`;

const TripAdvisorScript = styled(Html)`
  padding-bottom: ${(props) => props.theme.spacing.blockM};

  ul {
    padding: 0;
    list-style-type: none;
    margin: 0;

    li {
      list-style-type: none;
      margin: 0;
    }
  }
`;

const StyledImage = styled(Image)`
  img {
    max-width: none;
    width: 100%;
    margin-bottom: ${(props) => props.theme.spacing.elementXS};

    ${Media.greaterThan("s")`
      margin-bottom: 0;
    `}
  }
`;

const RoomTypeWrapper = styled.div`
  padding-bottom: ${(props) => props.theme.spacing.blockM};
`;

const MoreHotels = styled.div`
  padding-bottom: ${(props) => props.theme.spacing.blockL};

  ${Media.greaterThan("s")`
    padding-bottom: 220px;
  `}

  h3 {
    ${(props) => props.theme.fonts.h5}
    font-size: 20px;
    margin-bottom: ${(props) => props.theme.spacing.contentXL};

    ${Media.greaterThan("s")`
      margin-bottom: ${(props) => props.theme.spacing.elementXS};
    `}
  }
`;

const CardGridElement = styled(GridElement)`
  margin-bottom: ${(props) => props.theme.spacing.elementM};

  &:last-child {
    margin-bottom: 0;
  }

  ${Media.greaterThan("s")`
    margin-bottom: 0;
  `}
`;
